import { useCallback, useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

import { TextPrimary, TextSecondary, TicketCard } from "theme";
import {
  useWalletProvider,
  useChain,
  useNetworkHook,
  useWithdrawals,
} from "dapp-sdk";

import UndelegationMessage from "@components/UndelegationMessage";

import { PageContext } from "@providers/PageProvider";

import usePageData from "@hooks/usePageData";

import ClaimTypes from "./Components/ClaimTypes/ClaimTypes";
import { RADIO_BUTTONS } from "./constants";
import { TxType } from "./types";
import { getWithdrawalData } from "./helpers";

interface ClaimProps {
  onUnstakeClick: () => void;
}

const Claim = ({ onUnstakeClick }: ClaimProps) => {
  const [withdrawType, setWithDrawType] = useState(RADIO_BUTTONS[0].value);

  let withdrawals = useWithdrawals();
  if (!withdrawals) {
    withdrawals = [];
  }

  const { handleClaim } = useWalletProvider();
  const { status, switchChain } = useChain();
  const { switchNetwork } = useNetworkHook();

  const data = useContext(PageContext);

  const { isSafeMode } = data;

  const {
    undelegation_warning_message,
    switch_network_cta = "Switch Network",
    withdraw_cta = "Withdraw",
    erc20_token,
    no_withdraw_requests_title = "No requests found",
    no_withdraw_requests_description,
    no_withdraw_requests_description_cta,
    days_to_wait_for_withdraw = 15,
    is_withdraw_tabs_options = false,
    undelegation_warning_message_safe_mode,
  } = usePageData("$.page.data")[0] || {};

  const claimedTransactions = useMemo(() => {
    return withdrawals.filter((t: TxType) => t.isClaimable).reverse();
  }, [withdrawals]);

  const unClaimedTransactions = useMemo(() => {
    return withdrawals.filter((t: TxType) => !t.isClaimable);
  }, [withdrawals]);

  const onRadioValueChange = (value: string) => {
    setWithDrawType(value);
  };

  const withdrawData = is_withdraw_tabs_options
    ? withdrawType === "active"
      ? unClaimedTransactions
      : claimedTransactions
    : withdrawals;

  const handleWithdraw = (index: string, amount: string) => {
    handleClaim(Number(index), amount);
  };

  const tableRows = useMemo(() => {
    return (
      <Tr fontWeight="700" fontSize="12px">
        {withdrawType === "active" ? (
          <>
            <Th textTransform="none">Amount</Th>
            <Th textTransform="none" textAlign="right">
              Estimated time
            </Th>
          </>
        ) : (
          <>
            <Th textTransform="none">Amount</Th>
            <Th textTransform="none" textAlign="right">
              Withdrew on
            </Th>
          </>
        )}
      </Tr>
    );
  }, [withdrawType]);

  const getWithdrawActionField = useCallback(
    (isClaimable: boolean, message: string, amount: string, index: string) => {
      if (isSafeMode) {
        return "NA";
      } else if (isClaimable) {
        return (
          <Button
            fontWeight="700"
            fontSize={{ base: "10px", md: "12px" }}
            bg="primary"
            color="white"
            _dark={{
              bg: "white",
              color: "#142528",
            }}
            p="4px 12px"
            h="2rem"
            onClick={() => handleWithdraw(index, amount)}
          >
            {withdraw_cta}
          </Button>
        );
      }
      return message;
    },
    [handleWithdraw, isSafeMode, withdraw_cta]
  );

  return (
    <TicketCard
      p={{
        base: "1.5rem 1rem 1rem",
        xl: "2rem",
      }}
      minH={{ base: "371px", md: "395px" }}
    >
      {is_withdraw_tabs_options ? (
        <ClaimTypes
          radioButtons={RADIO_BUTTONS}
          onRadioChange={onRadioValueChange}
        />
      ) : null}

      {withdrawData && withdrawData?.length > 0 ? (
        withdrawType === "active" && !status && switchNetwork ? (
          <Center
            flexDirection="column"
            p={{
              base: "3rem 1rem 1rem",
              xl: "5rem 2rem 2.5rem",
            }}
            textAlign="center"
          >
            <Text
              color="textPrimary"
              fontWeight="600"
              fontSize={{
                base: "20px",
                xl: "24px",
              }}
              textAlign="center"
            >
              {no_withdraw_requests_title}
            </Text>

            <Button
              color="white"
              height="56px"
              mt="4rem"
              w="100%"
              fontWeight="600"
              fontSize="16px"
              borderRadius="12px"
              onClick={switchChain}
            >
              {switch_network_cta}
            </Button>
          </Center>
        ) : (
          <Flex flexDirection="column">
            {undelegation_warning_message ||
            (isSafeMode && undelegation_warning_message_safe_mode) ? (
              <Box mb={{ base: "1.5rem", md: "2rem" }}>
                <UndelegationMessage
                  message={
                    isSafeMode
                      ? undelegation_warning_message_safe_mode
                      : undelegation_warning_message
                  }
                />
              </Box>
            ) : null}

            <Box>
              <TableContainer
                overflowY="auto"
                mt={is_withdraw_tabs_options ? "2rem" : 0}
                height={
                  is_withdraw_tabs_options
                    ? { base: "250px", xl: "234px" }
                    : "320px"
                }
              >
                <Table variant="simple" p="0">
                  <Thead>{tableRows}</Thead>

                  <Tbody>
                    {withdrawData?.map((withdrawal: TxType) => {
                      const { updatedDate, index, isClaimable, amount } =
                        getWithdrawalData(
                          withdrawal,
                          days_to_wait_for_withdraw
                        );

                      return (
                        <Tr key={index} fontWeight="400" color="textSecondary">
                          <Td
                            fontSize="14px"
                            color="textPrimary"
                            fontWeight="600"
                          >
                            {amount}{" "}
                            <Text fontWeight="400" as="span">
                              {erc20_token}
                            </Text>
                          </Td>

                          <Td
                            fontSize={{ base: "10px", md: "12px" }}
                            textAlign="right"
                            whiteSpace="pre-line"
                          >
                            {getWithdrawActionField(
                              isClaimable,
                              updatedDate,
                              amount,
                              index
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </Flex>
        )
      ) : (
        <Center
          flexDirection="column"
          p={{
            base: "3rem 1rem 1rem",
            xl: "5rem 2rem 2.5rem",
          }}
          textAlign="center"
        >
          <TextPrimary
            fontWeight="600"
            fontSize={{
              base: "20px",
              xl: "24px",
            }}
            textAlign="center"
          >
            {no_withdraw_requests_title}
          </TextPrimary>

          {no_withdraw_requests_description && (
            <TextSecondary
              mt="2rem"
              fontWeight="400"
              fontSize={{
                base: "12px",
                xl: "14px",
              }}
            >
              {no_withdraw_requests_description}
              {no_withdraw_requests_description_cta ? (
                <Link color="primary" onClick={onUnstakeClick}>
                  {no_withdraw_requests_description_cta}
                </Link>
              ) : null}
            </TextSecondary>
          )}
        </Center>
      )}
    </TicketCard>
  );
};

export default Claim;
